"use client";

import { useEffect, useState } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { env } from "@/env";
import { Vote } from "@/types/script";
import { formatNumber } from "@/lib/format-number";

type VoteButtonsProps = {
  votes: Vote[];
  slug: string;
  next: () => void;
};

export default function VoteButtons({ votes, slug, next }: VoteButtonsProps) {
  const initialLikes = votes.filter((v) => v.type === "Like").length;
  const initialDislikes = votes.filter((v) => v.type === "Dislike").length;

  const [likes, setLikes] = useState(initialLikes);
  const [dislikes, setDislikes] = useState(initialDislikes);
  const [loading, setLoading] = useState(false);
  const [userVote, setUserVote] = useState<"like" | "dislike" | null>(null);
  const { user, accessToken } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      const userVote = votes.find((v) => v.userId === user.id);

      if (userVote) {
        setUserVote(userVote.type.toLowerCase() as "like" | "dislike");
      }
    }
  }, [user]);

  async function handleVote(type: "like" | "dislike") {
    if (!user) {
      return toast({
        title: "Authentication required",
        description: "Please sign in to vote",
        variant: "destructive",
      });
    }

    if (loading) return;

    setLoading(true);

    try {
      const response = await fetch(
        `${env.NEXT_PUBLIC_API_URL}/post/${slug}/${type}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ type }),
        },
      );

      if (!response.ok) throw new Error();

      if (userVote === type) {
        setUserVote(null);
        type === "like" ? setLikes((l) => l - 1) : setDislikes((d) => d - 1);
      } else {
        if (userVote) {
          type === "like"
            ? (setLikes((l) => l + 1), setDislikes((d) => d - 1))
            : (setDislikes((d) => d + 1), setLikes((l) => l - 1));
        } else {
          type === "like" ? setLikes((l) => l + 1) : setDislikes((d) => d + 1);
        }
        setUserVote(type);
        next();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to vote",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <span className="flex flex-row items-center rounded-md border border-input text-muted-foreground">
      <button
        onClick={() => handleVote("like")}
        disabled={loading ?? userVote === "like"}
        className={cn(
          "btn inline-flex h-9 gap-1.5 rounded-l-md bg-background px-4 py-2 shadow-sm hover:bg-accent hover:text-accent-foreground",
          userVote === "like" && "bg-white/10",
        )}
      >
        <ThumbsUp size={16} /> {formatNumber(likes)}
      </button>
      <hr className="h-9 border-r border-input" />
      <button
        onClick={() => handleVote("dislike")}
        disabled={loading ?? userVote === "dislike"}
        className={cn(
          "btn inline-flex h-9 gap-1.5 rounded-r-md bg-background px-4 py-2 shadow-sm hover:bg-accent hover:text-accent-foreground",
          userVote === "dislike" && "bg-white/10",
        )}
      >
        <ThumbsDown size={16} /> {formatNumber(dislikes)}
      </button>
    </span>
  );
}
